import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Switch } from '@dfds-pax/shared-components/client'

import { useVehicles } from '../../Hooks/vehicles/useVehicles'

import { ActiveSelectField, useActiveSelectField } from '../../context/ActiveSelectFieldProvider'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { focusFirstListOption } from '../../Utils/list-keyboard'
import OverlayFooter from '../shared/OverlayFooter'
import Categories from './Categories'
import Selector from './Selector'
interface Props {
  dataAttrList: { name: string; value: string }[]
  isLoading: boolean
  onCheckMultipleVehicle: (checked: boolean) => void
  onEscape?: () => void
  onFooterClick: () => void
}
const Vehicles: React.FC<Props> = ({ dataAttrList, isLoading, onCheckMultipleVehicle, onEscape, onFooterClick }) => {
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>()
  const { t } = useTranslation()
  const { activeField } = useActiveSelectField()
  const containerRef = useRef<HTMLDivElement>(null)
  const {
    dispatch,
    state: { residentVehicleChecked },
  } = useTravelSearchContext()
  const { residentCheckboxVisible, selectedVehicle } = useVehicles()
  const isNoCarSelected = selectedVehicle?.code === 'NCAR'

  const isOpen = activeField === ActiveSelectField.VEHICLE

  useEffect(() => {
    if (!isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedCategory(undefined)
      return
    }
    focusFirstListOption(containerRef.current)
  }, [isOpen, selectedCategory])

  const dataAttrProps = {} as {
    dataAttrName: string
    dataAttrValue: string
  }
  dataAttrList.map((dataAttr) => {
    const key = `data-e2e-${dataAttr?.name}`
    const dataAttrKey = key as keyof {
      dataAttrName: string
      dataAttrValue: string
    }
    dataAttrProps[dataAttrKey] = dataAttr.value?.toString() ?? ''
  })
  const keyLoading = `data-e2e-vehicle-loading`
  const dataAttrKeyLoading = keyLoading as keyof {
    dataAttrName: string
    dataAttrValue: string
  }
  dataAttrProps[dataAttrKeyLoading] = isLoading.toString()

  function onSelectCategory(categoryCode: string | undefined) {
    if (categoryCode === 'NCAR') {
      dispatch({
        type: 'SET_VEHICLE',
        vehicle: {
          categoryCode: undefined,
          code: 'NCAR',
          count: 0,
          trailer: undefined,
        },
      })
      dispatch({
        supported: false,
        type: 'SET_MULTIPLE_VEHICLES_SUPPORTED',
      })
      dispatch({
        checked: false,
        type: 'SET_MULTIPLE_VEHICLES_CHECKED',
      })
    } else setSelectedCategory(categoryCode)
  }

  function setResidentVehicleChecked(checked: boolean) {
    dispatch({ checked, type: 'SET_RESIDENT_VEHICLE_CHECKED' })
  }

  return (
    <div
      className="lg:max-h-[calc(100dvh-160px)] lg:min-w-[512px] lg:overflow-auto"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onEscape?.()
      }}
      ref={containerRef}
    >
      {residentCheckboxVisible ? (
        <div className="flex flex-col gap-1 border-0 border-b border-solid border-b-semantic-global-border-light px-semantic-global-md py-4">
          <div className="flex items-center gap-2">
            <Switch
              aria-labelledby="resident-vehicle-label"
              checked={residentVehicleChecked}
              disabled={!residentCheckboxVisible || isNoCarSelected}
              name="resident-vehicle"
              onChange={setResidentVehicleChecked}
            />
            <label
              id="resident-vehicle-label"
              className={isNoCarSelected ? 'cursor-not-allowed text-semantic-global-disabled-default' : 'cursor-pointer'}
              onClick={() => {
                if (isNoCarSelected) return
                setResidentVehicleChecked(!residentVehicleChecked)
              }}
            >
              {t('RESIDENT-VEHICLE-DISCOUNT')}
            </label>
          </div>
          {isNoCarSelected ? <div className="text-semantic-global-primary-darkest">{t('RESIDENT-VEHICLE-DISABLED-NO-CAR')}</div> : null}
        </div>
      ) : null}
      <Categories onSelectCategory={onSelectCategory} selectedCategoryCode={selectedCategory} />
      <Selector
        onCheckMultipleVehicle={onCheckMultipleVehicle}
        onSelectCategory={onSelectCategory}
        selectedCategoryCode={selectedCategory}
      />
      <OverlayFooter onClick={onFooterClick} />
    </div>
  )
}

export default Vehicles
